import {
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QuotesService } from '../quotes.service';
import { Blueprint } from '../../models/blueprint.model';
import { BlueprintItem } from '../../models/blueprint-item.model';
import { BlueprintItemViewerComponent } from './blueprint-item-viewer/blueprint-item-viewer.component';
import { Subscription } from 'rxjs';
import {
  NamedBlueprintSuggestion,
  ResolvedBlueprintSuggestion,
  blueprintSuggestionPercent,
  resolveNamedBlueprintSuggestions,
} from './blueprint-suggestions';

@Component({
  selector: 'app-blueprints-selector',
  imports: [CommonModule, BlueprintItemViewerComponent],
  templateUrl: './blueprints-selector.component.html',
  styleUrl: './blueprints-selector.component.scss',
})
export class BlueprintsSelectorComponent
  implements OnInit, OnChanges, OnDestroy
{
  private quotesService = inject(QuotesService);
  blueprintMap: Map<string, Blueprint> = new Map();
  private subscriptions = new Subscription();
  searchTerm = '';
  filteredBlueprints: Blueprint[] = [];
  visibleSuggestions: ResolvedBlueprintSuggestion<Blueprint>[] = [];
  otherBlueprints: Blueprint[] = [];
  private suggestedBlueprintIds = new Set<string>();

  @Input() selectedBlueprintId: string | null = null;
  /**
   * Blueprints to show. Omit the input to load via QuotesService.
   * An explicit empty list stays empty and is not replaced by that load.
   */
  @Input() blueprints?: Blueprint[];
  /** Name matches from a confirmed drawing. Empty for ordinary blueprint picking. */
  @Input() nameSuggestions: NamedBlueprintSuggestion[] = [];

  @Output() blueprintSelected = new EventEmitter<Blueprint | null>();

  expandedBlueprintsId: Set<string> = new Set();

  ngOnInit() {
    this.updateBlueprintMap();
    this.applySearchFilter();
    if (this.blueprints == null) {
      this.loadBlueprints();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blueprints'] || changes['nameSuggestions']) {
      this.updateBlueprintMap();
      this.applySearchFilter();
    }
  }

  suggestionPercent(confidence: number): string {
    return blueprintSuggestionPercent(confidence);
  }

  loadBlueprints(): void {
    this.subscriptions.add(
      this.quotesService.getBlueprints$().subscribe({
        next: (blueprints: Blueprint[]) => {
          this.blueprints = blueprints;
          this.updateBlueprintMap();
          this.applySearchFilter();
          // After updating the map, check if we need to load any referenced blueprints
          this.loadReferencedBlueprints();
        },
        error: (error: unknown) => {
          console.error('Error loading blueprints:', error);
        },
      }),
    );
  }

  toggleExpand(blueprint: Blueprint): void {
    if (this.expandedBlueprintsId.has(blueprint.id)) {
      this.expandedBlueprintsId.delete(blueprint.id);
    } else {
      this.expandedBlueprintsId.add(blueprint.id);
    }
  }

  isExpanded(blueprint: Blueprint): boolean {
    return this.expandedBlueprintsId.has(blueprint.id);
  }

  isSelected(blueprintId: string): boolean {
    return this.selectedBlueprintId === blueprintId;
  }

  onSearchTermChanged(value: string): void {
    this.searchTerm = value;
    this.applySearchFilter();
  }

  private updateBlueprintMap(): void {
    // Create a map for quick lookup of referenced blueprints
    // Ensure IDs are strings for consistent lookup
    // Create a new Map instance to trigger change detection
    const newMap = new Map<string, Blueprint>();
    for (const bp of this.blueprints ?? []) {
      newMap.set(String(bp.id), bp);
    }
    this.blueprintMap = newMap;
  }

  private applySearchFilter(): void {
    const blueprints = this.blueprints ?? [];
    const normalized = this.searchTerm.trim().toLowerCase();
    const matchesSearch = (blueprint: Blueprint) =>
      !normalized || blueprint.name.toLowerCase().includes(normalized);

    this.filteredBlueprints = blueprints.filter(matchesSearch);
    const resolved = resolveNamedBlueprintSuggestions(
      blueprints,
      this.nameSuggestions,
    );
    this.suggestedBlueprintIds = new Set(
      resolved.map((match) => match.blueprint.id),
    );
    this.visibleSuggestions = resolved.filter((match) =>
      matchesSearch(match.blueprint),
    );
    this.otherBlueprints = this.filteredBlueprints.filter(
      (blueprint) => !this.suggestedBlueprintIds.has(blueprint.id),
    );
  }

  /**
   * Collect all referenced blueprint IDs from all blueprint items
   */
  private collectReferencedBlueprintIds(): Set<string> {
    const referencedIds = new Set<string>();

    for (const blueprint of this.blueprints ?? []) {
      if (blueprint.items && blueprint.items.length > 0) {
        for (const item of blueprint.items) {
          if (item.linked_to_blueprint_id) {
            referencedIds.add(item.linked_to_blueprint_id);
          }
        }
      }
    }

    return referencedIds;
  }

  /**
   * Load any referenced blueprints that aren't already in the blueprintMap
   */
  private loadReferencedBlueprints(): void {
    const referencedIds = this.collectReferencedBlueprintIds();
    const missingIds: string[] = [];

    for (const id of referencedIds) {
      if (!this.blueprintMap.has(id)) {
        missingIds.push(id);
      }
    }

    if (missingIds.length > 0) {
      // Reload all blueprints to get the missing ones
      // This ensures we have all referenced blueprints
      this.subscriptions.add(
        this.quotesService.getBlueprints$().subscribe({
          next: (allBlueprints: Blueprint[]) => {
            // Merge new blueprints into existing ones, avoiding duplicates
            const currentBlueprints = this.blueprints ?? [];
            const existingIds = new Set(currentBlueprints.map((bp) => bp.id));
            const newBlueprints = allBlueprints.filter(
              (bp) => !existingIds.has(bp.id),
            );

            if (newBlueprints.length > 0) {
              this.blueprints = [...currentBlueprints, ...newBlueprints];
              this.updateBlueprintMap();
              this.applySearchFilter();
            }
          },
          error: (error: unknown) => {
            console.error('Error loading referenced blueprints:', error);
          },
        }),
      );
    }
  }

  /**
   * Get root items (items with no parent) for a blueprint, sorted by sibling_order
   */
  getRootItems(blueprint: Blueprint): BlueprintItem[] {
    if (!blueprint.items || blueprint.items.length === 0) {
      return [];
    }
    return blueprint.items
      .filter((item) => item.parent_blueprint_item_id === null)
      .sort((a, b) => a.sibling_order - b.sibling_order);
  }

  onBlueprintClick(blueprint: Blueprint) {
    // If clicking the already selected blueprint, unselect it
    if (this.selectedBlueprintId === blueprint.id) {
      this.blueprintSelected.emit(null);
    } else {
      this.blueprintSelected.emit(blueprint);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
